// Active Projects  (Tom Clancy 2026-08-05)
// ------------------------------------------------------------------
// The curated "what are we actually working on" view. Filters Sage's
// 316 In-Progress jobs down to the ~197 that the field team is really
// touching, and gives PMs a frictionless side-panel to enter Pre-Final
// and Final start/end dates. Dates cross-reference back to the main
// WIP (see Projects.js) via a computed projected_completion_date that
// prefers Final End when set.
//
// Related backend routes (server/routes/active-wip.js):
//   GET   /api/wip/active
//   PATCH /api/projects/:id/active-wip
//   PATCH /api/projects/:id/phase-dates
//
// Permissions: management, finance, PMs, salespeople can edit (both
// the Active toggle and the phase dates). Everyone else is read-only.
// See canEditActiveWip in server/middleware/auth.js.

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box, Paper, Typography, Chip, TextField, InputAdornment, Table, TableHead, TableRow,
  TableCell, TableBody, TableSortLabel, Tooltip, IconButton, Drawer, Button, Stack,
  Divider, Alert, LinearProgress, MenuItem, Select, FormControl, InputLabel, Switch,
  FormControlLabel, Snackbar, Link,
} from '@mui/material';
import {
  Search as SearchIcon, Edit as EditIcon, CheckCircle as CheckCircleIcon,
  ErrorOutlined as ErrorOutlineIcon, RadioButtonUnchecked as CircleIcon,
  RemoveCircle as RemoveIcon, FileDownload as DownloadIcon,
  History as HistoryIcon, Warning as WarningIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  getActiveWip, getInactiveWip, setProjectActiveWip, setProjectPhaseDates,
  getActiveWipAudit, exportActiveWipUrl,
} from '../services/api';

// -----------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------
const fmtDate = (d) => d ? String(d).slice(0, 10) : '';
const fmtMoney = (n) => (n == null ? '' :
  Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }));

const DISCIPLINE_COLORS = {
  'AV / HT': '#1565C0',
  'Lighting': '#F9A825',
  'Shades': '#6A1B9A',
  'IT': '#2E7D32',
  'Other': '#757575',
};

// Roles that can edit — mirrors canEditActiveWip on the server side.
function useCanEdit() {
  const { user } = useAuth();
  return useMemo(() => {
    if (!user) return false;
    const role = user.role || '';
    const roles = Array.isArray(user.roles) ? user.roles : [];
    const editRoles = new Set([
      'Management',
      'PM', 'Project Manager',
      'Sales',
      'Field Operations', 'Field Operations Manager', 'Production Manager',
    ]);
    if (role === 'Management' || roles.includes('Management')) return true;
    if (editRoles.has(role)) return true;
    if (roles.some(r => editRoles.has(r))) return true;
    // Finance access ids (server: FINANCIAL_ACCESS_IDS)
    const financeIds = new Set([1, 2, 3, 4, 5, 6, 7, 8, 16, 18, 19, 60, 61, 69, 90, 93]);
    return financeIds.has(user.id);
  }, [user]);
}

// -----------------------------------------------------------------
// Row status icon
// -----------------------------------------------------------------
function DatesStatusIcon({ status, prefinalOverdue, finalOverdue }) {
  if (finalOverdue || prefinalOverdue) {
    return (
      <Tooltip title={finalOverdue ? 'Final End date is in the past' : 'Pre-Final End date is in the past'}>
        <WarningIcon fontSize="small" sx={{ color: '#C62828' }} />
      </Tooltip>
    );
  }
  if (status === 'complete') {
    return <Tooltip title="All four dates entered"><CheckCircleIcon fontSize="small" sx={{ color: '#2E7D32' }} /></Tooltip>;
  }
  if (status === 'partial') {
    return <Tooltip title="Some dates missing"><ErrorOutlineIcon fontSize="small" sx={{ color: '#E65100' }} /></Tooltip>;
  }
  return <Tooltip title="No dates entered yet"><CircleIcon fontSize="small" sx={{ color: '#9E9E9E' }} /></Tooltip>;
}

// -----------------------------------------------------------------
// Edit Dates side panel
// -----------------------------------------------------------------
function DatesDrawer({ open, onClose, project, onSaved, canEdit, onOpenProjectPage }) {
  const [preStart, setPreStart] = useState('');
  const [preEnd,   setPreEnd]   = useState('');
  const [finStart, setFinStart] = useState('');
  const [finEnd,   setFinEnd]   = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [audit, setAudit] = useState([]);
  const [showAudit, setShowAudit] = useState(false);

  useEffect(() => {
    if (!project) return;
    setPreStart(fmtDate(project.prefinal_start));
    setPreEnd(fmtDate(project.prefinal_end));
    setFinStart(fmtDate(project.final_start));
    setFinEnd(fmtDate(project.final_end));
    setErr(''); setShowAudit(false); setAudit([]);
  }, [project]);

  const loadAudit = async () => {
    if (!project) return;
    try {
      const { data } = await getActiveWipAudit(project.id);
      setAudit(data.entries || []);
      setShowAudit(true);
    } catch (e) { console.error(e); }
  };

  const save = async () => {
    if (!project) return;
    setSaving(true); setErr('');
    try {
      const payload = {
        prefinal_start: preStart || null,
        prefinal_end:   preEnd   || null,
        final_start:    finStart || null,
        final_end:      finEnd   || null,
      };
      const { data } = await setProjectPhaseDates(project.id, payload);
      onSaved(data.project);
    } catch (e) {
      setErr(e.response?.data?.error || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const removeFromActive = async () => {
    if (!project) return;
    if (!window.confirm(`Move "${project.name}" out of the Active Projects list?`)) return;
    try {
      await setProjectActiveWip(project.id, false, 'Marked inactive from Active Projects page');
      onSaved({ ...project, is_active_wip: false, __removed: true });
    } catch (e) {
      setErr(e.response?.data?.error || e.message || 'Toggle failed');
    }
  };

  if (!project) return null;
  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: 480 } }}>
      <Box sx={{ p: 3 }}>
        <Stack direction="row" alignItems="flex-start" spacing={1}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6">{project.name}</Typography>
            <Typography variant="caption" color="text.secondary">
              {project.job_number} · {project.discipline} · PM: {project.pm || '—'} · Sales: {project.salesman || '—'}
            </Typography>
          </Box>
          <Tooltip title="Open the full project page">
            <Button
              size="small"
              variant="outlined"
              startIcon={<OpenInNewIcon />}
              onClick={() => onOpenProjectPage(project)}
            >
              Project Page
            </Button>
          </Tooltip>
        </Stack>

        <Divider sx={{ my: 2 }} />

        {/*
          Date fields: label above the input instead of MUI's floating label
          overlay. The overlay conflicts with the browser's native date picker
          calendar/arrow icon and makes the popup look layered on top of the
          input value (Tom Clancy 2026-08-05).
        */}
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Pre-Final</Typography>
        <Stack direction="row" spacing={2}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary">Start</Typography>
            <TextField
              type="date" size="small" fullWidth disabled={!canEdit}
              value={preStart} onChange={(e) => setPreStart(e.target.value)}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary">End</Typography>
            <TextField
              type="date" size="small" fullWidth disabled={!canEdit}
              value={preEnd} onChange={(e) => setPreEnd(e.target.value)}
            />
          </Box>
        </Stack>

        <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>Final</Typography>
        <Stack direction="row" spacing={2}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary">Start</Typography>
            <TextField
              type="date" size="small" fullWidth disabled={!canEdit}
              value={finStart} onChange={(e) => setFinStart(e.target.value)}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary">End</Typography>
            <TextField
              type="date" size="small" fullWidth disabled={!canEdit}
              value={finEnd} onChange={(e) => setFinEnd(e.target.value)}
            />
          </Box>
        </Stack>

        {err && <Alert severity="error" sx={{ mt: 2 }}>{err}</Alert>}

        <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
          <Button variant="contained" onClick={save} disabled={!canEdit || saving}>Save</Button>
          <Button variant="text" onClick={onClose}>Cancel</Button>
          <Box sx={{ flex: 1 }} />
          {canEdit && (
            <Tooltip title="Move this job out of Active Projects">
              <Button color="warning" size="small" startIcon={<RemoveIcon />} onClick={removeFromActive}>
                Remove from Active
              </Button>
            </Tooltip>
          )}
        </Stack>

        <Divider sx={{ my: 3 }} />
        <Button size="small" startIcon={<HistoryIcon />} onClick={loadAudit}>
          {showAudit ? 'Refresh audit' : 'Show Active toggle history'}
        </Button>
        {showAudit && (
          <Box sx={{ mt: 1, maxHeight: 200, overflow: 'auto', fontSize: 12 }}>
            {audit.length === 0 && <Typography variant="caption">No toggle history.</Typography>}
            {audit.map(e => (
              <Box key={e.id} sx={{ py: 0.5, borderBottom: '1px solid #eee' }}>
                <strong>{fmtDate(e.changed_at)}</strong> — {e.user_name || 'system'}: {e.previous ? 'Active' : 'Inactive'} → {e.new_value ? 'Active' : 'Inactive'}
                {e.note ? <> ·  <em>{e.note}</em></> : null}
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Drawer>
  );
}

// -----------------------------------------------------------------
// Main page
// -----------------------------------------------------------------
const COLUMNS = [
  { id: 'status',       label: '',                sortable: false, width: 40 },
  { id: 'job_number',   label: 'Job #',           sortable: true },
  { id: 'name',         label: 'Project',         sortable: true },
  { id: 'discipline',   label: 'Discipline',      sortable: true, width: 110 },
  { id: 'pm',           label: 'PM',              sortable: true },
  { id: 'salesman',     label: 'Sales',           sortable: true },
  { id: 'current_phase',label: 'Phase',           sortable: true, width: 70 },
  { id: 'prefinal_start', label: 'Pre-Final Start', sortable: true },
  { id: 'prefinal_end',   label: 'Pre-Final End',   sortable: true },
  { id: 'final_start',    label: 'Final Start',   sortable: true },
  { id: 'final_end',      label: 'Final End',     sortable: true },
  { id: 'contract_value', label: 'Contract',      sortable: true, align: 'right' },
];

export default function ActiveProjects() {
  const canEdit = useCanEdit();
  const navigate = useNavigate();
  const [showInactive, setShowInactive] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [q, setQ] = useState('');
  const [pmFilter, setPmFilter] = useState('');
  const [salesFilter, setSalesFilter] = useState('');
  const [disciplineFilter, setDisciplineFilter] = useState('');
  const [missingOnly, setMissingOnly] = useState(false);
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('final_end');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [toast, setToast] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const { data } = showInactive ? await getInactiveWip() : await getActiveWip();
      setRows(data.projects || []);
    } catch (e) {
      setErr(e.response?.data?.error || e.message || 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => { load(); }, [load]);

  const pms = useMemo(() =>
    Array.from(new Set(rows.map(r => r.pm).filter(Boolean))).sort(), [rows]);
  const sales = useMemo(() =>
    Array.from(new Set(rows.map(r => r.salesman).filter(Boolean))).sort(), [rows]);
  const disciplines = useMemo(() =>
    Array.from(new Set(rows.map(r => r.discipline).filter(Boolean))).sort(), [rows]);

  const filtered = useMemo(() => {
    let out = rows;
    if (q) {
      const needle = q.toLowerCase();
      out = out.filter(r =>
        (r.name || '').toLowerCase().includes(needle) ||
        (r.job_number || '').toLowerCase().includes(needle) ||
        (r.pm || '').toLowerCase().includes(needle) ||
        (r.salesman || '').toLowerCase().includes(needle));
    }
    if (pmFilter) out = out.filter(r => r.pm === pmFilter);
    if (salesFilter) out = out.filter(r => r.salesman === salesFilter);
    if (disciplineFilter) out = out.filter(r => r.discipline === disciplineFilter);
    if (missingOnly) out = out.filter(r => r.dates_status !== 'complete');
    if (overdueOnly) out = out.filter(r => r.prefinal_overdue || r.final_overdue);

    const cmp = (a, b) => {
      const av = a[orderBy]; const bv = b[orderBy];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return av - bv;
      return String(av).localeCompare(String(bv));
    };
    out = [...out].sort((a, b) => order === 'asc' ? cmp(a, b) : -cmp(a, b));
    return out;
  }, [rows, q, pmFilter, salesFilter, disciplineFilter, missingOnly, overdueOnly, order, orderBy]);

  const counts = useMemo(() => ({
    total: rows.length,
    complete: rows.filter(r => r.dates_status === 'complete').length,
    partial: rows.filter(r => r.dates_status === 'partial').length,
    empty: rows.filter(r => r.dates_status === 'empty').length,
    prefinalOverdue: rows.filter(r => r.prefinal_overdue).length,
    finalOverdue: rows.filter(r => r.final_overdue).length,
  }), [rows]);

  const handleSort = (col) => {
    if (orderBy === col) setOrder(order === 'asc' ? 'desc' : 'asc');
    else { setOrderBy(col); setOrder('asc'); }
  };
  const openRow = (row) => { setSelected(row); setDrawerOpen(true); };
  const openProjectPage = (row) => {
    // Full project detail lives at /projects/:id (ProjectDetail.js)
    navigate(`/projects/${row.id}`);
  };
  const onSaved = (proj) => {
    if (proj?.__removed) {
      setRows(rs => rs.filter(r => r.id !== proj.id));
      setToast(`Moved "${proj.name}" out of Active`);
    } else if (proj) {
      setRows(rs => rs.map(r => r.id === proj.id ? proj : r));
      setToast('Saved');
    }
    setDrawerOpen(false);
  };
  const restoreToActive = async (row) => {
    try {
      await setProjectActiveWip(row.id, true, 'Restored to Active from inactive list');
      setRows(rs => rs.filter(r => r.id !== row.id));
      setToast(`Restored "${row.name}" to Active`);
    } catch (e) {
      setToast(e.response?.data?.error || 'Restore failed');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {showInactive ? 'Inactive Projects' : 'Active Projects'}
        </Typography>
        <Chip label={`${filtered.length} / ${counts.total}`} size="small" />
        {!showInactive && counts.finalOverdue > 0 && (
          <Chip
            size="small" color="error"
            icon={<WarningIcon />}
            label={`${counts.finalOverdue} past Final End`}
            onClick={() => setOverdueOnly(true)}
          />
        )}
        {!showInactive && counts.prefinalOverdue > 0 && (
          <Chip
            size="small" color="warning"
            icon={<WarningIcon />}
            label={`${counts.prefinalOverdue} past Pre-Final End`}
            onClick={() => setOverdueOnly(true)}
          />
        )}
        {!showInactive && (counts.partial + counts.empty > 0) && (
          <Chip
            size="small"
            label={`${counts.partial + counts.empty} missing dates`}
            onClick={() => setMissingOnly(true)}
          />
        )}
        <Box sx={{ flex: 1 }} />
        <FormControlLabel
          control={<Switch size="small" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />}
          label="Show Inactive"
        />
        <Button
          startIcon={<DownloadIcon />}
          component="a"
          href={`https://eiyuhwgj.gensparkclaw.com/downloads/active-projects-labor-by-discipline.xlsx?t=${Date.now()}`}
          target="_blank"
          rel="noopener"
          variant="outlined"
          color="primary"
          size="small"
        >Labor by Discipline</Button>
        <Button
          startIcon={<DownloadIcon />}
          component="a"
          href={exportActiveWipUrl()}
          variant="outlined"
          size="small"
        >Excel</Button>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            size="small" placeholder="Search job / name / PM / sales"
            value={q} onChange={(e) => setQ(e.target.value)}
            InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) }}
            sx={{ minWidth: 280 }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>PM</InputLabel>
            <Select label="PM" value={pmFilter} onChange={(e) => setPmFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {pms.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Sales</InputLabel>
            <Select label="Sales" value={salesFilter} onChange={(e) => setSalesFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {sales.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Discipline</InputLabel>
            <Select label="Discipline" value={disciplineFilter} onChange={(e) => setDisciplineFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {disciplines.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControlLabel
            control={<Switch size="small" checked={missingOnly} onChange={(e) => setMissingOnly(e.target.checked)} />}
            label="Missing dates only"
          />
          <FormControlLabel
            control={<Switch size="small" checked={overdueOnly} onChange={(e) => setOverdueOnly(e.target.checked)} />}
            label="Overdue only"
          />
        </Stack>
      </Paper>

      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
      {loading && <LinearProgress />}

      <Paper variant="outlined">
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {COLUMNS.map(col => (
                  <TableCell key={col.id}
                    align={col.align || 'left'}
                    sx={{ width: col.width, whiteSpace: 'nowrap' }}
                  >
                    {col.sortable ? (
                      <TableSortLabel
                        active={orderBy === col.id}
                        direction={orderBy === col.id ? order : 'asc'}
                        onClick={() => handleSort(col.id)}
                      >{col.label}</TableSortLabel>
                    ) : col.label}
                  </TableCell>
                ))}
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map(r => (
                <TableRow
                  key={r.id} hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => openRow(r)}
                >
                  <TableCell>
                    <DatesStatusIcon
                      status={r.dates_status}
                      prefinalOverdue={r.prefinal_overdue}
                      finalOverdue={r.final_overdue}
                    />
                  </TableCell>
                  <TableCell>{r.job_number}</TableCell>
                  <TableCell>{r.name}</TableCell>
                  <TableCell>
                    <Chip size="small" label={r.discipline}
                      sx={{ backgroundColor: DISCIPLINE_COLORS[r.discipline] || '#757575', color: 'white' }}
                    />
                  </TableCell>
                  <TableCell>{r.pm || '—'}</TableCell>
                  <TableCell>{r.salesman || '—'}</TableCell>
                  <TableCell>{r.current_phase}</TableCell>
                  <TableCell>{fmtDate(r.prefinal_start)}</TableCell>
                  <TableCell sx={{ color: r.prefinal_overdue ? '#C62828' : 'inherit', fontWeight: r.prefinal_overdue ? 600 : 400 }}>
                    {fmtDate(r.prefinal_end)}
                  </TableCell>
                  <TableCell>{fmtDate(r.final_start)}</TableCell>
                  <TableCell sx={{ color: r.final_overdue ? '#C62828' : 'inherit', fontWeight: r.final_overdue ? 600 : 400 }}>
                    {fmtDate(r.final_end)}
                  </TableCell>
                  <TableCell align="right">{fmtMoney(r.contract_value)}</TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    {showInactive ? (
                      <Button size="small" onClick={() => restoreToActive(r)} disabled={!canEdit}>
                        Restore
                      </Button>
                    ) : (
                      <IconButton size="small" onClick={() => openRow(r)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!loading && filtered.length === 0 && (
                <TableRow><TableCell colSpan={COLUMNS.length + 1} sx={{ textAlign: 'center', p: 3 }}>
                  <em>No projects match current filters.</em>
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Paper>

      <DatesDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        project={selected}
        onSaved={onSaved}
        canEdit={canEdit}
        onOpenProjectPage={openProjectPage}
      />

      <Snackbar
        open={!!toast} autoHideDuration={3000}
        onClose={() => setToast('')} message={toast}
      />
    </Box>
  );
}
